<!--
  The one-time window that tells a person about a new feature. The shared prop
  `featureAnnouncement` has the key, and announcements.ts has the content for
  it. The window stamps the render, and the stamp ends it for good: the X, the
  Esc key, a click outside and the button all close it with no second step.
-->
<template>
  <BaseModal
    v-if="announcement"
    :name="FEATURE_ANNOUNCEMENT_MODAL_NAME"
    width-class="max-w-[500px]"
    modal-class="pt-16 sm:pt-32"
    is-background-blurred
    :auto-focus="false"
    @show="stampSeen"
  >
    <div class="relative px-5 pt-8 pb-7 text-center sm:px-10 sm:pb-8">
      <button
        type="button"
        class="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
        aria-label="Close"
        test_id="feature-announcement-close"
        @click="close"
      >
        <svg
          class="h-4 w-4"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          aria-hidden="true"
        >
          <path d="M3 3l10 10M13 3L3 13" />
        </svg>
      </button>

      <component :is="announcement.picture" />

      <div class="display-1 mt-6 text-2xl sm:text-3xl">
        {{ announcement.title }}
      </div>
      <p class="mt-3 text-sm text-neutral-600">{{ announcement.line }}</p>

      <div
        class="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-2"
      >
        <BaseButton
          color="primary"
          size="large"
          test_id="feature-announcement-button"
          @click="open"
        >
          {{ announcement.button }}
        </BaseButton>
        <button
          v-if="announcement.secondary"
          type="button"
          class="rounded px-2 py-2 text-sm font-semibold text-neutral-600 hover:text-neutral-900"
          test_id="feature-announcement-secondary"
          @click="close"
        >
          {{ announcement.secondary }}
        </button>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { router, usePage } from '@inertiajs/vue3'
import {
  LIVE_NOW_PATH,
  liveNowPath,
} from '@/modules/instant-sessions/liveNowPath'
import axios from 'axios'
import BaseButton from '@/shared/components/BaseButton.vue'
import BaseModal from '@/shared/components/BaseModal.vue'
import { useModals } from '@/shared/composables/useModals.js'
import { FEATURE_ANNOUNCEMENT_MODAL_NAME } from '@/shared/data/globalVariables.js'
import { ANNOUNCEMENTS } from '@/shared/components/modals/feature-announcement/announcements'
import { startFeatureTour } from '@/modules/feature-tour/useFeatureTour'

const page = usePage()

// Live now moves under Mentors with the exo-live flag.
const hrefOf = (href: string) =>
  href === LIVE_NOW_PATH
    ? liveNowPath(
        (page.props.shared as { featureFlags?: { exoLiveEnabled?: boolean } })
          ?.featureFlags,
      )
    : href
const { closeModal } = useModals()

// Read once, when the page loads. The stamp turns the prop to false on the
// next visit, and the window must stay open while the person reads it.
const key = (page.props.shared as { featureAnnouncement?: string | false })
  .featureAnnouncement
const announcement = key ? ANNOUNCEMENTS[key] : undefined

const stampSeen = () => {
  axios.post(`/feature-interactions/${key}/seen`).catch(() => {})
}

const close = () => closeModal(FEATURE_ANNOUNCEMENT_MODAL_NAME)

// The button opens the page of the feature, or starts its tour on this page.
const open = () => {
  close()
  if (announcement?.tour) startFeatureTour(announcement.tour)
  else if (announcement?.href) router.visit(hrefOf(announcement.href))
}
</script>
